import { dropOffCoordinates } from "../core/drop-off";

/**
 * Somewhere a car can be sent.
 *
 * A station most mornings, a house on the way back. `StationSummary` satisfies it as it stands,
 * which is why the type is shaped this way rather than as a union.
 */
export interface NavTarget {
	id: string;
	name: string;
	lat: number;
	lon: number;
	/** Preferred when present: a street address is what the driver would have typed. */
	address?: string | null;
}

/**
 * Where a car is actually told to go.
 *
 * A station's own pin is the middle of the platforms, which underground is somewhere no car can
 * reach. What the driver wants is the curb he stops at. Coordinates come from `core/drop-off.ts`,
 * which routing shares; an address is only listed here when it reads better than a point.
 */
const DROP_OFF_ADDRESS: Record<string, string> = {
	GLEN: "571 Bosworth St, San Francisco, CA",
};

function destinationFor(target: NavTarget): string {
	const address = target.address ?? DROP_OFF_ADDRESS[target.id];
	if (address) {
		return address;
	}
	const curb = dropOffCoordinates(target.id);
	return curb ? `${curb.lat},${curb.lon}` : `${target.lat},${target.lon}`;
}

/**
 * Works anywhere, and is what the link actually points at.
 *
 * Waze is the one the driver wants, but see the note at the foot of this file: an app link that
 * cannot be detected is a link that silently goes nowhere. So the href stays a URL every phone
 * can open, and `wazeNavigationUrl` is offered where a real gesture is doing the opening.
 */
export function googleMapsNavigationUrl(target: NavTarget): string {
	const url = new URL("https://www.google.com/maps/dir/");
	url.searchParams.set("api", "1");
	url.searchParams.set("destination", destinationFor(target));
	url.searchParams.set("travelmode", "driving");
	url.searchParams.set("dir_action", "navigate");
	return url.toString();
}

/**
 * Waze, by its universal link rather than its `waze://` scheme.
 *
 * The https form opens the app when it is installed and the website when it is not, so there is
 * nothing to detect and no silent dead end — which is exactly what the removed Google Maps
 * hand-off could not manage.
 *
 * Always a point, never the address: `ul?q=` is a search box, and a search is a guess. The whole
 * reason these destinations exist is that the curb is not where the pin is.
 */
export function wazeNavigationUrl(target: NavTarget): string {
	const curb = dropOffCoordinates(target.id) ?? { lat: target.lat, lon: target.lon };
	return `https://waze.com/ul?ll=${curb.lat}%2C${curb.lon}&navigate=yes`;
}

/*
 * There was a `comgooglemaps://` hand-off here, to make sure the drive landed in Google Maps
 * rather than the phone's default map app. It is gone, and should not come back in that shape.
 *
 * The scheme cannot be feature-detected — with the app missing it silently does nothing — so it
 * needed a timer to decide whether to follow up with the web link. There is no honest value for
 * that timer: on 2026-08-27 the fallback fired at 903 ms with the page still visible and nothing
 * suspended, because iOS was simply still switching apps. It then opened a browser view, and
 * swiping back from Google Maps landed on a blank Safari page with a search bar, every time.
 *
 * The https URL above is a Google Maps universal link and iOS hands it to the app directly, which
 * is what these links did before the scheme was introduced. A link that is left alone works.
 */
